import { useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Bell } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ChatBellSection, useChatBellState } from "@/components/chat/chat-bell-section";
import { NotificationCenter } from "@/components/notifications/notification-center";
import { useCalendarNotificationSync } from "@/hooks/hr/calendar/use-calendar-notification-sync";
import { useNotifications, useNotificationBrowserAlerts, useNotificationSummary, useUnreadCount } from "@/hooks/notifications/use-notifications";
import { ROUTES } from "@/routes/paths";

/**
 * O sino: um ponto no cabeçalho (vermelho se há críticas por ler) e, ao abrir, o centro de notificações
 * compacto — filtros, agrupamento por prioridade e acções na linha. «Ver todas» leva à página.
 */
export function NotificationBell() {
  const [open, setOpen] = useState(false);

  useNotificationBrowserAlerts();

  // Chegou um convite, uma resposta, um cancelamento…: o selo do Calendário na sidebar atualiza logo.
  const { data: recent } = useNotifications(50);
  useCalendarNotificationSync(recent);
  const { data: unreadData, refetch: refetchUnread } = useUnreadCount();
  const { data: summary, refetch: refetchSummary } = useNotificationSummary();

  // O chat (`platform.chat`) junta os seus avisos ao mesmo sino; sem a feature não muda nada.
  const chat = useChatBellState();
  const unreadCount = (unreadData?.count ?? 0) + chat.unread;
  const criticalUnread = summary?.by_severity.critical ?? 0;

  // Ao abrir, refrescamos contadores na hora — o que se vê está sempre atual, sem esperar pelo poll de 30 s.
  const handleOpenChange = useCallback(
    (next: boolean) => {
      setOpen(next);
      if (next) {
        refetchUnread();
        refetchSummary();
      }
    },
    [refetchUnread, refetchSummary],
  );

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          aria-label={criticalUnread > 0 ? `Notificações: ${criticalUnread} crítica${criticalUnread !== 1 ? "s" : ""} por ler` : unreadCount > 0 ? `Notificações: ${unreadCount} por ler` : "Notificações"}
          className="relative h-9 w-9 rounded-lg text-muted-foreground hover:bg-foreground/4 hover:text-foreground"
        >
          <Bell className="h-[18px] w-[18px]" strokeWidth={1.75} />
          {/* Um ponto, não um número num círculo: o que interessa é «há coisas novas» — e vermelho se alguma é crítica. */}
          {unreadCount > 0 && (
            <span className={`absolute right-2 top-2 h-1.5 w-1.5 rounded-full ring-2 ring-background ${criticalUnread > 0 ? "bg-destructive" : "bg-primary"}`} />
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[min(420px,calc(100vw-1.5rem))] overflow-hidden rounded-xl border-border/60 p-0" sideOffset={10}>
        <div className="flex items-center justify-between border-b border-border/60 px-4 py-3">
          <p className="text-[13px] font-medium">Notificações</p>
          <Link
            to={ROUTES.NOTIFICATIONS}
            onClick={() => setOpen(false)}
            className="rounded-md px-1.5 py-0.5 text-[12px] text-muted-foreground hover:bg-foreground/4 hover:text-foreground"
          >
            Ver todas
          </Link>
        </div>
        {chat.enabled && <ChatBellSection onNavigate={() => setOpen(false)} />}
        <NotificationCenter variant="popover" onNavigate={() => setOpen(false)} />
      </PopoverContent>
    </Popover>
  );
}
